import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Search, Download, Trash2, Mail, Users, UserCheck, RotateCcw } from "lucide-react";
import {
  adminLogin, getStats, getNewsletter, getRegistrations,
  setNewsletterStatus, setRegistrationStatus, deleteNewsletter, deleteRegistration, exportCsv,
} from "../api";
import { BRAND } from "../mock";

const TOKEN_KEY = "wynridge_token";

function Login({ onLogin }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const res = await adminLogin(u, p);
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch (e2) { setErr(e2?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="ops-login">
      <form className="ops-login-card" onSubmit={submit}>
        <img className="logo" src={BRAND.logoInk} alt="Wynridge" style={{ height: 28, marginBottom: 8 }} />
        <span className="eyebrow">Operations</span>
        <h1 className="display" style={{ fontSize: "1.8rem", margin: "6px 0 20px" }}>Admin sign in</h1>
        <label>Username</label>
        <input value={u} onChange={(e) => setU(e.target.value)} placeholder="admin" autoFocus />
        <label>Password</label>
        <input type="password" value={p} onChange={(e) => setP(e.target.value)} placeholder="Enter password" />
        {err && <p className="rf-error">{err}</p>}
        <button className="pill pill-dark" disabled={loading} style={{ justifyContent: "center", marginTop: 6 }}>{loading ? "Signing in\u2026" : "Sign In"}</button>
        <Link to="/" className="ops-back">← Back to site</Link>
      </form>
    </div>
  );
}

export default function Ops() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("registrations");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }, []);

  const load = useCallback(async (search = "") => {
    if (!token) return;
    setLoading(true);
    try {
      const s = await getStats(token); setStats(s.data);
      const res = tab === "newsletter" ? await getNewsletter(token, search) : await getRegistrations(token, search);
      setRows(res.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally { setLoading(false); }
  }, [token, tab, logout]);

  useEffect(() => { load(q); /* eslint-disable-next-line */ }, [token, tab]);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  const toggleStatus = async (row) => {
    if (tab === "newsletter") {
      const next = row.status === "subscribed" ? "unsubscribed" : "subscribed";
      await setNewsletterStatus(token, row.id, next);
    } else {
      const next = row.status === "new" ? "unsubscribed" : "new";
      await setRegistrationStatus(token, row.id, next);
    }
    load(q);
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this record permanently?")) return;
    if (tab === "newsletter") await deleteNewsletter(token, row.id);
    else await deleteRegistration(token, row.id);
    load(q);
  };

  const doExport = async () => {
    const kind = tab === "newsletter" ? "newsletter" : "registrations";
    const res = await exportCsv(token, kind);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `wynridge_${kind}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "";

  if (!token) return <Login onLogin={setToken} />;

  const s = stats || { newsletter: {}, registrations: {} };

  return (
    <div className="ops">
      <header className="ops-header">
        <div className="ops-brand"><img src={BRAND.logoInk} alt="Wynridge" style={{ height: 24 }} /><span className="ops-tag">Ops</span></div>
        <button className="ops-logout" onClick={logout}><LogOut size={16} /> Sign out</button>
      </header>

      <div className="ops-body">
        <div className="ops-stats">
          <div className="ops-stat"><Users size={20} /><div><span className="n">{s.registrations.total ?? 0}</span><span className="l">Registrations</span></div></div>
          <div className="ops-stat"><UserCheck size={20} /><div><span className="n">{s.registrations.new ?? 0}</span><span className="l">New leads</span></div></div>
          <div className="ops-stat"><Mail size={20} /><div><span className="n">{s.newsletter.total ?? 0}</span><span className="l">Newsletter</span></div></div>
          <div className="ops-stat"><UserCheck size={20} /><div><span className="n">{s.newsletter.subscribed ?? 0}</span><span className="l">Subscribed</span></div></div>
        </div>

        <div className="ops-tabs">
          <button className={tab === "registrations" ? "active" : ""} onClick={() => setTab("registrations")}>Registrations</button>
          <button className={tab === "newsletter" ? "active" : ""} onClick={() => setTab("newsletter")}>Newsletter</button>
        </div>

        <div className="ops-toolbar">
          <form className="ops-search" onSubmit={onSearch}>
            <Search size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "newsletter" ? "Search email\u2026" : "Search name, email, city\u2026"} />
          </form>
          <button className="ops-export" onClick={doExport}><Download size={16} /> Export CSV</button>
        </div>

        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              {tab === "newsletter" ? (
                <tr><th>Email</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
              ) : (
                <tr><th>Name</th><th>Email</th><th>Phone</th><th>City</th><th>Province</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
              )}
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={tab === "newsletter" ? 4 : 8} className="ops-empty">No records yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  {tab === "newsletter" ? (
                    <><td>{r.email}</td></>
                  ) : (
                    <><td>{r.name}</td><td>{r.email}</td><td>{r.phone}</td><td>{r.city}</td><td>{r.province}</td></>
                  )}
                  <td><span className={`ops-badge ${(r.status === "subscribed" || r.status === "new") ? "on" : "off"}`}>{r.status}</span></td>
                  <td>{fmt(r.created_at)}</td>
                  <td className="ops-actions">
                    <button title="Toggle status" onClick={() => toggleStatus(r)}><RotateCcw size={15} /></button>
                    <button title="Delete" className="del" onClick={() => remove(r)}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
